import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginModal } from './LoginModal';
import { toast } from 'react-toastify';

export const UserMenu: React.FC = () => {
  const { user, loading, logout } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [avatarError, setAvatarError] = useState(false);
  const avatarRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click/touch — no backdrop needed
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        avatarRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) return;
      setDropdownOpen(false);
    };

    // Small delay so the opening tap doesn't immediately re-close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('touchstart', handleOutside);
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [dropdownOpen]);

  // Calculate dropdown position from avatar's screen coordinates
  const openDropdown = useCallback(() => {
    if (!avatarRef.current) return;
    const rect = avatarRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY + 8,
      right: window.innerWidth - rect.right,
    });
    setDropdownOpen(true);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    toast.info('Signed out.', { theme: 'dark' });
  };

  if (loading) return null;

  return (
    <>
      <div className="user-menu-item">
        {!user ? (
          <button className="signin-button" onClick={() => setModalOpen(true)}>
            Sign In
          </button>
        ) : (
          <button
            ref={avatarRef}
            className="user-avatar-wrapper"
            onClick={openDropdown}
            aria-label="Open user menu"
            aria-expanded={dropdownOpen}
          >
            {user.picture && !avatarError ? (
              <img
                src={user.picture}
                alt={user.name}
                className="user-avatar"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <span className="user-avatar-initials">
                {user.name.charAt(0).toUpperCase()}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Dropdown — portal so it's never clipped by any parent */}
      {dropdownOpen && user && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="user-dropdown"
          style={{ position: 'absolute', top: dropdownPos.top, right: dropdownPos.right }}
        >
          <div className="user-dropdown-header">
            <div className="user-dropdown-avatar-circle">
              {user.picture && !avatarError ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <span className="user-avatar-initials">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="user-dropdown-info">
              <span className="user-dropdown-name">{user.name}</span>
              <span className="user-dropdown-email">{user.email}</span>
            </div>
          </div>
          <hr className="user-dropdown-divider" />
          <Link to="/my-saved-crosshairs" className="user-dropdown-link" onClick={() => setDropdownOpen(false)}>
            Saved crosshairs
          </Link>
          <Link to="/my-crosshairs" className="user-dropdown-link" onClick={() => setDropdownOpen(false)}>
            My crosshairs
          </Link>
          {user.isAdmin && (
            <Link to="/admin/crosshair-moderation" className="user-dropdown-link" onClick={() => setDropdownOpen(false)}>
              Moderate crosshairs
            </Link>
          )}
          {user.isAdmin && (
            <Link to="/admin/contact-messages" className="user-dropdown-link" onClick={() => setDropdownOpen(false)}>
              Contact messages
            </Link>
          )}
          {user.isAdmin && (
            <Link to="/admin/user-daily-stats" className="user-dropdown-link" onClick={() => setDropdownOpen(false)}>
              User daily stats
            </Link>
          )}
          <button className="user-dropdown-signout" onClick={handleLogout}>
            <svg className="user-dropdown-signout__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out
          </button>
        </div>,
        document.body
      )}

      {modalOpen && <LoginModal onClose={() => setModalOpen(false)} />}
    </>
  );
};
